import axios from 'axios';
import type {
  TodayResponse,
  BqFilter,
  BqInsightsResponse,
  FtueProgressionResponse,
  FtueQuery,
  PmQuery,
  ProductMetricsResponse,
  MonetizationQuery,
  MonetizationResult,
  MonetizationTab,
  AutoplayConfig,
  LifecyclePushRun,
  LifecyclePushRunReport,
  LifecyclePushStatsRow,
  LifecyclePushVariantRow,
  LifecyclePushStatus,
  LifecycleTriggerKey,
  BattingRulesConfigResponse,
  DailyEngagementConfig,
  DailyEngagementConfigResponse,
  DailyEngagementFastClock,
  CreateWebhookRequest,
  UpdateWebhookRequest,
  TransferAccountRequest,
  MergeAccountRequest,
  PurchaseSearchFilters,
  VoidedSyncCounts,
  GoogleRecheckResult,
  DailyDealConfig,
  DailyDealAnalytics,
  ResetCooldownRequest,
  RegenerateSeedRequest,
  UpdateDailyDealConfigRequest,
  DailyDealPurchaseHistoryResponse,
  PurchaseHistoryFilters,
  UserDailyDealsStatus,
  ExportFilters,
  AppVersionMapping,
  CreateAppVersionMapping,
  UpdateAppVersionMapping,
  ConfigVersion,
  CreateConfigVersion,
  UpdateConfigVersion,
  CloneConfigVersion,
  GroupAnalyticsParams,
  SessionStatsRow,
  MatchesPerUserRow,
  CohortBreakdownRow,
  ModeMixRow,
  CardDropsRow,
  AiCompetitivenessRow,
  IapLtvRow,
  DailyReportAdsResponse,
  DailyReportParams,
  DailyReportResponse,
  MatchdayDirectorConfigPayload,
  MatchdayDirectorConfigResponse,
  MatchdayDirectorConfigHistoryParams,
  MatchdayDirectorConfigHistoryResponse,
  PlayerOwnershipStatus,
  PlayerMaster,
  CohortRecalculationStatus,
  CohortSpendReport,
  RiskListName,
} from '@/types';
import type {
  AddSchedulesFromFixturesResult,
  CalendarFestival,
  CalendarFestivalBody,
  CreateCricketFixtureBody,
  CricketCalendarSettings,
  CricketFixture,
  CricketFixtureList,
  CricketFixtureQuery,
  CricketSeries,
  CricketSyncStatus,
  MatchPushLanguageTest,
  MatchPushPlan,
  MatchPushPlanResults,
  MatchPushPreviewItem,
  MatchPushQueueQuery,
  MatchPushResultsRow,
  MatchPushSampleBody,
  MatchPushSampleResult,
  MatchPushSettings,
  MatchPushStatus,
  MatchPushTemplate,
  MatchPushTemplateBody,
  PlayerLocaleCoverage,
  UpdateCricketFixtureBody,
  UpdateMatchPushPlanBody,
  UpdateMatchPushSettingsBody,
} from '@/types/matchPushes';
import type {
  PvpBallLogDto,
  PvpConfig,
  PvpLiveDto,
  PvpLiveQuery,
  PvpEndRequestDto,
  PvpMatchDetailDto,
  PvpMatchesDto,
  PvpMatchesQuery,
  PvpPlayerDto,
  PvpReviewList,
  PvpReviewPlayer,
  PvpReviewQuery,
  PvpSyncDto,
  PvpSyncQuery,
  UpdatePvpConfigBody,
  UpdatePvpReviewBody,
} from '@/types/pvp';
import type {
  ExpV2Action,
  ExpV2ActionBody,
  ExpV2ActionResult,
  ExpV2AddOverridesBody,
  ExpV2AddOverridesResult,
  ExpV2Counts,
  ExpV2Population,
  ExpV2Results,
  ExpV2Timeline,
  ExpV2DecisionBody,
  ExpV2Detail,
  ExpV2DraftBody,
  ExpV2DraftUpdateBody,
  ExpV2HistoryEntry,
  ExpV2Layer,
  ExpV2Override,
  ExpV2Test,
  ExpV2UserList,
  ExpV2UserListDetail,
} from '@/types/experimentsV2';

const API_PREFIX = import.meta.env.VITE_API_PREFIX || 'api';
const API_VERSION = import.meta.env.VITE_API_VERSION || 'v1';
let API_BASE_URL = import.meta.env.VITE_API_URL || 'http://localhost:3000';

// Ensure API_BASE_URL has a protocol
if (API_BASE_URL && !API_BASE_URL.startsWith('http')) {
  API_BASE_URL = `https://${API_BASE_URL}`;
}

const API_URL = `${API_BASE_URL}/${API_PREFIX}/${API_VERSION}`;

export const axiosInstance = axios.create({
  baseURL: API_URL,
  headers: {
    'Content-Type': 'application/json',
  },
});

// Roster-size response shape from /admin/config/roster-size.
export interface RosterSizeResponse {
  totalPlayers: number;
  activePlayers: number;
  maxPlayerIndex: number;
  byRole: {
    batters: number;
    bowlers: number;
    allRounders: number;
    wicketKeepers: number;
  };
  winnerAlliance: number;
}

// Module-scoped cache so multiple admin components fetch this at most once
// per session. Concurrent callers share the in-flight promise. The roster
// changes only when an admin edits player_masters; a full page reload (or
// adminApi.invalidateRosterSize()) refetches.
let rosterSizeCache: Promise<{ data: RosterSizeResponse }> | null = null;

// Request interceptor - add auth token
axiosInstance.interceptors.request.use(
  (config) => {
    const token = localStorage.getItem('admin_token');
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

// Response interceptor - handle token refresh and errors
axiosInstance.interceptors.response.use(
  (response) => response,
  async (error) => {
    const originalRequest = error.config;

    // Handle 401 Unauthorized
    if (error.response?.status === 401 && !originalRequest._retry) {
      originalRequest._retry = true;

      const refreshToken = localStorage.getItem('admin_refresh_token');

      if (refreshToken) {
        try {
          const response = await axios.post(`${API_URL}/admin/auth/refresh`, {
            refreshToken,
          });

          const { accessToken } = response.data;
          localStorage.setItem('admin_token', accessToken);

          originalRequest.headers.Authorization = `Bearer ${accessToken}`;
          return axiosInstance(originalRequest);
        } catch (refreshError) {
          // Refresh failed, logout
          localStorage.removeItem('admin_token');
          localStorage.removeItem('admin_refresh_token');
          localStorage.removeItem('admin_user');
          window.location.href = '/login';
          return Promise.reject(refreshError);
        }
      } else {
        // No refresh token, logout
        localStorage.removeItem('admin_token');
        localStorage.removeItem('admin_user');
        window.location.href = '/login';
      }
    }

    // Handle 403 Forbidden — show an AWS-style "you have X, you need Y"
    // notification using the structured payload the backend now ships in
    // error.response.data.details. The notification module is imported
    // lazily so the api.ts file stays usable in non-browser contexts
    // (test runs, SSR, etc.).
    if (error.response?.status === 403) {
      try {
        const { showAccessErrorNotification } =
          await import('../components/AccessErrorNotification');
        showAccessErrorNotification(error.response?.data);
      } catch {
        // notification module unavailable — non-fatal
      }
    }

    return Promise.reject(error);
  }
);

// API helper functions
export const adminApi = {
  // Analytics
  getDashboard: (params?: Record<string, string | number | boolean>) =>
    axiosInstance.get('/admin/analytics/dashboard', { params }),

  getInstallSources: (params?: Record<string, string | number | boolean>) =>
    axiosInstance.get('/admin/analytics/install-sources', { params }),

  getKPI: (params?: Record<string, string | number | boolean>) =>
    axiosInstance.get('/admin/analytics/kpi', { params }),

  getRevenue: (params?: Record<string, string | number | boolean>) =>
    axiosInstance.get('/admin/analytics/revenue', { params }),

  // Analytics Module - dedicated endpoint for analytics page
  getAnalyticsModule: (params?: Record<string, string | number | boolean>) =>
    axiosInstance.get('/admin/analytics/dashboard', { params }),

  getActiveUsersSeries: (params?: { minutes?: number }) =>
    axiosInstance.get('/admin/analytics/active-users', { params }),

  getToday: () => axiosInstance.get<TodayResponse>('/admin/analytics/today'),

  /** BigQuery Insights for the last `days` days (7-90); cached 30 min server-side. */
  getBqInsights: (days: number, filter?: BqFilter) =>
    axiosInstance.get<BqInsightsResponse>('/admin/analytics/bq-insights', {
      params: {
        days,
        platform: filter && filter.platform !== 'all' ? filter.platform : undefined,
        version: filter?.version ?? undefined,
      },
    }),

  /**
   * FTUE & Progression: Day-0 funnel, progression tiles and the Level 2-10 ladder for one
   * filter, plus an optional compare view. No params = 0.4.4, all platforms, 17 Oct to today.
   */
  getFtueProgression: (params: FtueQuery) =>
    axiosInstance.get<FtueProgressionResponse>('/admin/analytics/ftue-progression', { params }),

  /** Product Metrics page (BigQuery rollups, rebuilt every 4 hours; cached 30 minutes server-side). */
  getProductMetrics: (params: PmQuery) =>
    axiosInstance.get<ProductMetricsResponse>('/admin/analytics/product-metrics', { params }),

  /**
   * Monetization page: one tab of the BigQuery rollups (`from` included, `to` excluded, IST days;
   * at most 92 days). Cached 15 minutes server-side. A 400 carries the reason in `message`.
   */
  getMonetization: <T>(tab: MonetizationTab, params: MonetizationQuery) =>
    axiosInstance.get<MonetizationResult<T>>(`/admin/monetization/${tab}`, { params }),

  getAcquisitionFunnel: (params: { from: string; to: string }) =>
    axiosInstance.get('/admin/analytics/acquisition-funnel', { params }),

  getAbandonmentBreakdown: (params: { from: string; to: string; matchType?: string }) =>
    axiosInstance.get('/admin/analytics/abandonment', { params }),

  getFtueFunnel: (params: { from: string; to: string }) =>
    axiosInstance.get('/admin/analytics/ftue-funnel', { params }),
  getSessionStats: (params: GroupAnalyticsParams) =>
    axiosInstance.get<SessionStatsRow[]>('/admin/analytics/session-stats', { params }),
  getMatchesPerUser: (params: GroupAnalyticsParams) =>
    axiosInstance.get<MatchesPerUserRow[]>('/admin/analytics/matches-per-user', { params }),
  getCohortBreakdown: (params: GroupAnalyticsParams) =>
    axiosInstance.get<CohortBreakdownRow[]>('/admin/analytics/cohort-breakdown', { params }),
  getModeMix: (params: GroupAnalyticsParams) =>
    axiosInstance.get<ModeMixRow[]>('/admin/analytics/mode-mix', { params }),
  getCardDrops: (params: GroupAnalyticsParams) =>
    axiosInstance.get<CardDropsRow[]>('/admin/analytics/card-drops', { params }),
  getAiCompetitiveness: (params: GroupAnalyticsParams) =>
    axiosInstance.get<AiCompetitivenessRow[]>('/admin/analytics/ai-competitiveness', { params }),
  getIapLtv: (params: GroupAnalyticsParams & { maxDay?: number }) =>
    axiosInstance.get<IapLtvRow[]>('/admin/analytics/ltv', { params }),

  /** The team's daily sheet, one entry per IST day in [from, to). 400 outside 1-92 days. */
  getDailyReport: (params: DailyReportParams) =>
    axiosInstance.get<DailyReportResponse>('/admin/analytics/daily-report', { params }),

  /** Daily report rows 43-44 (ad and total revenue, USD), from BigQuery. */
  getDailyReportAds: (params: DailyReportParams) =>
    axiosInstance.get<DailyReportAdsResponse>('/admin/analytics/daily-report/ads', { params }),

  getDivisionDistribution: () => axiosInstance.get('/admin/analytics/divisions'),

  getEconomyAnalytics: (params?: Record<string, string>) =>
    axiosInstance.get('/admin/analytics/economy', { params }),

  getEconomyPerDivision: () => axiosInstance.get('/admin/analytics/economy-per-division'),

  getRiskAnalytics: (params?: Record<string, string>) =>
    axiosInstance.get('/admin/analytics/risk', { params }),

  // AI Analytics
  getGlobalAiAnalytics: (params?: Record<string, string>) =>
    axiosInstance.get('/admin/analytics/ai', { params }),

  getUserAiAnalytics: (userId: string, params?: Record<string, string>) =>
    axiosInstance.get(`/admin/analytics/ai/user/${userId}`, { params }),

  // AI Batting Diagnostics — see AI-BATTING-DIAGNOSTICS-IMPLEMENTATION.md §6.2.
  // Each method optionally takes a userId for the per-player drill-down.
  getAiWicketsOverview: (params: Record<string, string | undefined> = {}) =>
    axiosInstance.get('/admin/analytics/ai/wickets/overview', { params }),

  getAiBounceClusters: (params: Record<string, string | number | undefined> = {}) =>
    axiosInstance.get('/admin/analytics/ai/wickets/bounce-clusters', {
      params,
    }),

  getAiBatsmanPositionClusters: (params: Record<string, string | number | undefined> = {}) =>
    axiosInstance.get('/admin/analytics/ai/wickets/batsman-position-clusters', { params }),

  getAiCatchAnalysis: (params: Record<string, string | undefined> = {}) =>
    axiosInstance.get('/admin/analytics/ai/wickets/catch-analysis', {
      params,
    }),

  getAiWicketsByPhysics: (params: Record<string, string | undefined> = {}) =>
    axiosInstance.get('/admin/analytics/ai/wickets/by-physics', { params }),

  getAiWicketsExtras: (params: Record<string, string | undefined> = {}) =>
    axiosInstance.get('/admin/analytics/ai/wickets/extras', { params }),

  getAiWicketEvents: (params: Record<string, string | number | undefined> = {}) =>
    axiosInstance.get('/admin/analytics/ai/wickets/events', { params }),

  exportAiWicketDiagnostics: (fromDate?: string, toDate?: string, userId?: string) =>
    axiosInstance.get('/admin/export/ai-wicket-diagnostics', {
      params: { fromDate, toDate, userId },
      responseType: 'blob',
    }),

  // Players
  searchPlayers: (params: Record<string, string | number | boolean>) =>
    axiosInstance.get('/admin/players', { params }),

  getPlayer: (id: string) => axiosInstance.get(`/admin/players/${id}`),

  updatePlayerStatus: (playerId: string, isActive: boolean) =>
    axiosInstance.patch(`/admin/players/${playerId}/status`, { isActive }),

  grantItems: (playerId: string, data: Record<string, unknown>) =>
    axiosInstance.post(`/admin/players/${playerId}/grant`, data),

  banPlayer: (playerId: string, data: Record<string, unknown>) =>
    axiosInstance.post(`/admin/players/${playerId}/ban`, data),

  unbanPlayer: (playerId: string, data: Record<string, unknown>) =>
    axiosInstance.post(`/admin/players/${playerId}/unban`, data),

  getBanHistory: (playerId: string) => axiosInstance.get(`/admin/players/${playerId}/bans`),

  deletePlayer: (playerId: string, password?: string) =>
    axiosInstance.delete(`/admin/players/${playerId}`, { data: { password } }),

  // Player Economy
  getPlayerEconomy: (playerId: string) => axiosInstance.get(`/admin/players/${playerId}/economy`),

  updatePlayerEconomy: (playerId: string, data: Record<string, unknown>) =>
    axiosInstance.patch(`/admin/players/${playerId}/economy`, data),

  grantCurrency: (playerId: string, data: Record<string, unknown>) =>
    axiosInstance.post(`/admin/players/${playerId}/economy/grant`, data),

  getCurrencyTransactions: (playerId: string, params?: Record<string, string | number | boolean>) =>
    axiosInstance.get(`/admin/players/${playerId}/economy/transactions`, { params }),

  // LiveOps
  sendNotification: (data: Record<string, unknown>) =>
    axiosInstance.post('/admin/liveops/notifications/send', data),

  getNotificationHistory: (params?: Record<string, string | number | boolean>) =>
    axiosInstance.get('/admin/liveops/notifications/history', { params }),

  getSegmentCount: (segment: Record<string, unknown>) =>
    axiosInstance.post('/admin/liveops/segments/count', segment),

  getLifecyclePushStatus: () =>
    axiosInstance.get<LifecyclePushStatus>('/admin/liveops/lifecycle-push/status'),

  updateLifecyclePushSettings: (body: { dailyCap?: number; autoEnabled?: boolean }) =>
    axiosInstance.put<LifecyclePushStatus>('/admin/liveops/lifecycle-push/settings', body),

  sendLifecyclePushSample: (trigger: LifecycleTriggerKey, count: number) =>
    axiosInstance.post<LifecyclePushRunReport>(
      `/admin/liveops/lifecycle-push/triggers/${trigger}/sample`,
      { count }
    ),

  // Runs the trigger on the replica: can take tens of seconds.
  countLifecyclePushTrigger: (trigger: LifecycleTriggerKey) =>
    axiosInstance.post<{ qualified: number; skippedLocal: number }>(
      `/admin/liveops/lifecycle-push/triggers/${trigger}/count`
    ),

  getLifecyclePushStats: (days = 7) =>
    axiosInstance.get<LifecyclePushStatsRow[]>('/admin/liveops/lifecycle-push/stats', {
      params: { days },
    }),

  getLifecyclePushVariants: (days = 7) =>
    axiosInstance.get<LifecyclePushVariantRow[]>('/admin/liveops/lifecycle-push/variants', {
      params: { days },
    }),

  getLifecyclePushRuns: (limit = 50) =>
    axiosInstance.get<LifecyclePushRun[]>('/admin/liveops/lifecycle-push/runs', {
      params: { limit },
    }),

  // Config
  getReadonlyModules: () =>
    axiosInstance.get<Record<string, boolean>>('/admin/config/readonly-modules'),

  getFeatureFlags: () => axiosInstance.get('/admin/config/feature-flags'),

  updateFeatureFlag: (flagName: string, value: boolean, reason?: string) =>
    axiosInstance.put(`/admin/config/feature-flags/${flagName}`, { value, reason }),

  getDivisionConfig: () => axiosInstance.get('/admin/config/divisions'),

  getLevelConfig: () => axiosInstance.get('/admin/config/levels'),

  // Roster size — totals + per-role breakdown + WA count.
  // Cached at module scope so the API is hit at most once per admin session
  // (and concurrent callers share the same in-flight promise). The roster
  // changes only when an admin edits player_masters; a full page reload
  // refetches it. Call `invalidateRosterSize()` to force a refresh.
  getRosterSize: () => {
    if (!rosterSizeCache) {
      rosterSizeCache = axiosInstance
        .get<RosterSizeResponse>('/admin/config/roster-size')
        .catch((err) => {
          // Don't poison the cache on failure — let the next caller retry.
          rosterSizeCache = null;
          throw err;
        });
    }
    return rosterSizeCache;
  },

  invalidateRosterSize: () => {
    rosterSizeCache = null;
  },

  // Maintenance
  getMaintenanceStatus: () => axiosInstance.get('/admin/config/maintenance'),

  toggleMaintenance: (enabled: boolean, message?: string, title?: string) =>
    axiosInstance.put('/admin/config/maintenance', { enabled, message, title }),

  // Config versions (the override bundles app versions point at)
  getConfigVersions: () => axiosInstance.get<ConfigVersion[]>('/admin/config/config-versions'),

  getConfigVersion: (id: string) =>
    axiosInstance.get<ConfigVersion>(`/admin/config/config-versions/${id}`),

  createConfigVersion: (data: CreateConfigVersion) =>
    axiosInstance.post<ConfigVersion>('/admin/config/config-versions', data),

  updateConfigVersion: (id: string, data: UpdateConfigVersion) =>
    axiosInstance.put<ConfigVersion>(`/admin/config/config-versions/${id}`, data),

  cloneConfigVersion: (id: string, data: CloneConfigVersion) =>
    axiosInstance.post<ConfigVersion>(`/admin/config/config-versions/${id}/clone`, data),

  deleteConfigVersion: (id: string) => axiosInstance.delete(`/admin/config/config-versions/${id}`),

  purgeConfigCache: (label?: string) =>
    axiosInstance.post<{ success: boolean; keysCleared: number }>(
      '/admin/config/config-versions/purge-cache',
      label ? { label } : {}
    ),

  // App version registry (app version -> config version mappings)
  getBuildConfigVersions: () =>
    axiosInstance.get<AppVersionMapping[]>('/admin/config/build-versions'),

  getBuildConfigVersion: (id: string) =>
    axiosInstance.get<AppVersionMapping>(`/admin/config/build-versions/${id}`),

  createBuildConfigVersion: (data: CreateAppVersionMapping) =>
    axiosInstance.post<AppVersionMapping>('/admin/config/build-versions', data),

  updateBuildConfigVersion: (id: string, data: UpdateAppVersionMapping) =>
    axiosInstance.put<AppVersionMapping>(`/admin/config/build-versions/${id}`, data),

  deleteBuildConfigVersion: (id: string) =>
    axiosInstance.delete(`/admin/config/build-versions/${id}`),

  // Force Update Config
  getForceUpdateConfig: () => axiosInstance.get('/admin/config/force-update'),

  updateForceUpdateConfig: (data: Record<string, unknown>) =>
    axiosInstance.put('/admin/config/force-update', data),

  // Ads Config Version
  getAdsConfigVersion: () => axiosInstance.get('/admin/config/ads-version'),

  updateAdsConfigVersion: (data: { configVersion: string }) =>
    axiosInstance.put('/admin/config/ads-version', data),

  adjustAdsConfigVersion: (body?: {
    segment?: 'major' | 'minor' | 'patch';
    direction?: 'up' | 'down';
  }) => axiosInstance.post('/admin/config/ads-version/bump', body ?? {}),

  // Custom Match JSON Version
  getCustomMatchJsonVersion: () => axiosInstance.get('/admin/config/custom-match-version'),

  updateCustomMatchJsonVersion: (data: { customMatchJsonVersion: string }) =>
    axiosInstance.put('/admin/config/custom-match-version', data),

  adjustCustomMatchJsonVersion: (body?: {
    segment?: 'major' | 'minor' | 'patch';
    direction?: 'up' | 'down';
  }) => axiosInstance.post('/admin/config/custom-match-version/bump', body ?? {}),

  // Card Generation rating bounds
  getCardGenerationConfig: () => axiosInstance.get('/admin/config/card-generation'),

  updateCardGenerationConfig: (data: {
    cardRatingBounds: Record<string, { min: number; max: number }>;
  }) => axiosInstance.put('/admin/config/card-generation', data),

  updateCardRarityWeights: (data: { cardRarityWeights: Record<string, number> }) =>
    axiosInstance.put('/admin/config/card-generation/rarity-weights', data),

  // Fan Rating soft-clamp curve config
  getFrCurveConfig: () => axiosInstance.get('/admin/config/fr-curve'),

  updateFrCurveConfig: (data: {
    maxStatFR?: number;
    curveStrength?: number;
    baseWinFactor?: number;
  }) => axiosInstance.put('/admin/config/fr-curve', data),

  // FR calculator — server computes FR under the live curve config
  calculateFr: (data: Record<string, number | boolean | string>) =>
    axiosInstance.post('/admin/config/fr-curve/calculate', data),

  // Blitz Showdown config (coinBaseValue, cardDivisor, matN/matchFactor/ratingOffsets)
  getBlitzConfig: () => axiosInstance.get('/admin/config/blitz-config'),

  updateBlitzConfig: (data: {
    coinBaseValue?: number;
    cardDivisor?: number;
    matN?: number[];
    matchFactor?: number[];
    ratingOffsets?: number[];
    entryCostTokens?: number;
    unlockLevel?: number;
  }) => axiosInstance.put('/admin/config/blitz-config', data),

  // Player autoplay price (coins per autoplayed innings); null clears it
  getAutoplayConfig: () => axiosInstance.get<AutoplayConfig>('/admin/config/autoplay'),

  updateAutoplayConfig: (coinCost: number | null) =>
    axiosInstance.put<AutoplayConfig>('/admin/config/autoplay', { coinCost }),

  // GDD batting rules document (replaced whole); null resets to the built-in copy
  getBattingRules: () =>
    axiosInstance.get<BattingRulesConfigResponse>('/admin/config/batting-rules'),

  updateBattingRules: (battingRules: Record<string, unknown> | null) =>
    axiosInstance.put<BattingRulesConfigResponse>('/admin/config/batting-rules', {
      battingRules,
    }),

  // Daily Engagement (Daily Reward, spin, coin bonus, freezes, loyalty): one
  // document, replaced whole (GET it, edit, PUT it back)
  getDailyEngagementConfig: () =>
    axiosInstance.get<DailyEngagementConfigResponse>('/admin/config/daily-engagement-config'),

  updateDailyEngagementConfig: (dailyEngagementConfig: DailyEngagementConfig) =>
    axiosInstance.put<DailyEngagementConfigResponse>('/admin/config/daily-engagement-config', {
      dailyEngagementConfig,
    }),

  // Daily Engagement fast clock for one player (staging only, 404 in
  // production). secondsPerDay: a number switches it on or changes its speed,
  // undefined keeps a running clock's speed (10 when switching on), null
  // switches it off AND resets the player's Daily Engagement state.
  getDailyEngagementFastClock: (userId: string) =>
    axiosInstance.get<DailyEngagementFastClock>(
      `/admin/daily-engagement/fast-clock/${encodeURIComponent(userId)}`
    ),

  setDailyEngagementFastClock: (userId: string, secondsPerDay?: number | null) =>
    axiosInstance.post<DailyEngagementFastClock>('/admin/daily-engagement/fast-clock', {
      userId,
      ...(secondsPerDay === undefined ? {} : { secondsPerDay }),
    }),

  // Active player masters by name or playerIndex (needs player_data:list)
  searchActivePlayerMasters: (search: string, limit = 20) =>
    axiosInstance.get<{ data: PlayerMaster[]; total: number }>('/admin/player-data', {
      params: { search, isActive: true, limit },
    }),

  // Admin Users
  listAdmins: (params?: {
    page?: number;
    limit?: number;
    roleType?: string;
    status?: string;
    search?: string;
    orderBy?: 'createdAt' | 'lastLoginAt' | 'email';
    orderDir?: 'ASC' | 'DESC';
  }) => axiosInstance.get('/admin/auth/admins', { params }),

  getAdminUser: (id: string) => axiosInstance.get(`/admin/auth/admins/${id}`),

  createAdminUser: (data: {
    email: string;
    password: string;
    firstName: string;
    lastName: string;
    roleType: string;
  }) => axiosInstance.post('/admin/auth/admins', data),

  updateAdminUser: (
    id: string,
    data: {
      firstName?: string;
      lastName?: string;
      roleType?: string;
      status?: string;
    }
  ) => axiosInstance.put(`/admin/auth/admins/${id}`, data),

  resetAdminPassword: (id: string, newPassword: string) =>
    axiosInstance.post(`/admin/auth/admins/${id}/reset-password`, {
      newPassword,
    }),

  listAdminRoles: () => axiosInstance.get('/admin/auth/roles'),

  queryAuditLogs: (params?: {
    adminId?: string;
    actionType?: string;
    targetType?: string;
    targetId?: string;
    startDate?: string;
    endDate?: string;
    success?: boolean;
    page?: number;
    limit?: number;
  }) => axiosInstance.get('/admin/audit-logs', { params }),

  // Custom Match — National Teams
  getCmNationalTeams: (params?: Record<string, string | number | boolean>) =>
    axiosInstance.get('/admin/cm-national-teams', { params }),

  getCmNationalTeam: (id: string) => axiosInstance.get(`/admin/cm-national-teams/${id}`),

  createCmNationalTeam: (data: Record<string, unknown>) =>
    axiosInstance.post('/admin/cm-national-teams', data),

  updateCmNationalTeam: (id: string, data: Record<string, unknown>) =>
    axiosInstance.put(`/admin/cm-national-teams/${id}`, data),

  deleteCmNationalTeam: (id: string) => axiosInstance.delete(`/admin/cm-national-teams/${id}`),

  // Tournaments
  getTournaments: (params?: Record<string, string | number | boolean>) =>
    axiosInstance.get('/admin/tournaments', { params }),

  createTournament: (data: Record<string, unknown>) =>
    axiosInstance.post('/admin/tournaments', data),

  cancelTournament: (id: string, reason: string) =>
    axiosInstance.post(`/admin/tournaments/${id}/cancel`, { reason }),

  // Webhooks
  getWebhooks: () => axiosInstance.get('/admin/webhooks'),

  getWebhook: (id: string) => axiosInstance.get(`/admin/webhooks/${id}`),

  getWebhookEvents: () => axiosInstance.get('/admin/webhooks/events'),

  createWebhook: (data: CreateWebhookRequest) => axiosInstance.post('/admin/webhooks', data),

  updateWebhook: (id: string, data: UpdateWebhookRequest) =>
    axiosInstance.put(`/admin/webhooks/${id}`, data),

  deleteWebhook: (id: string) => axiosInstance.delete(`/admin/webhooks/${id}`),

  activateWebhook: (id: string) => axiosInstance.post(`/admin/webhooks/${id}/activate`),

  deactivateWebhook: (id: string) => axiosInstance.post(`/admin/webhooks/${id}/deactivate`),

  testWebhook: (id: string) => axiosInstance.post(`/admin/webhooks/${id}/test`),

  getWebhookStats: (id: string) => axiosInstance.get(`/admin/webhooks/${id}/stats`),

  getWebhookLogs: (id: string, params?: Record<string, string | number | boolean>) =>
    axiosInstance.get(`/admin/webhooks/${id}/logs`, { params }),

  getAllWebhookLogs: (params?: Record<string, string | number | boolean>) =>
    axiosInstance.get('/admin/webhooks/logs/all', { params }),

  getWebhookLogById: (logId: string) => axiosInstance.get(`/admin/webhooks/logs/${logId}`),

  // Live Events
  getLiveEvents: (params?: Record<string, string | number | boolean>) =>
    axiosInstance.get('/admin/live-events', { params }),

  getLiveEvent: (id: string) => axiosInstance.get(`/admin/live-events/${id}`),

  createLiveEvent: (data: Record<string, unknown>) =>
    axiosInstance.post('/admin/live-events', data),

  updateLiveEvent: (id: string, data: Record<string, unknown>) =>
    axiosInstance.put(`/admin/live-events/${id}`, data),

  deleteLiveEvent: (id: string) => axiosInstance.delete(`/admin/live-events/${id}`),

  // Live Event Match Schedules
  getMatchSchedules: (eventId: string) =>
    axiosInstance.get(`/admin/live-events/${eventId}/schedules`),

  createMatchSchedule: (eventId: string, data: Record<string, unknown>) =>
    axiosInstance.post(`/admin/live-events/${eventId}/schedules`, data),

  updateMatchSchedule: (eventId: string, scheduleId: string, data: Record<string, unknown>) =>
    axiosInstance.put(`/admin/live-events/${eventId}/schedules/${scheduleId}`, data),

  deleteMatchSchedule: (eventId: string, scheduleId: string) =>
    axiosInstance.delete(`/admin/live-events/${eventId}/schedules/${scheduleId}`),

  setRealWinner: (eventId: string, scheduleId: string, winnerTeamCode: string) =>
    axiosInstance.post(`/admin/live-events/${eventId}/schedules/${scheduleId}/winner`, {
      winnerTeamCode,
    }),

  // Live Events: pick matches from the cricket calendar
  addSchedulesFromFixtures: (eventId: string, fixtureIds: string[]) =>
    axiosInstance.post<AddSchedulesFromFixturesResult>(
      `/admin/live-events/${eventId}/schedules/from-fixtures`,
      { fixtureIds }
    ),

  // Cricket calendar (Live Ops)
  cricketCalendar: {
    listFixtures: (params?: CricketFixtureQuery) =>
      axiosInstance.get<CricketFixtureList>('/admin/cricket-calendar/fixtures', { params }),

    getFixture: (id: string) =>
      axiosInstance.get<CricketFixture>(`/admin/cricket-calendar/fixtures/${id}`),

    createFixture: (body: CreateCricketFixtureBody) =>
      axiosInstance.post<CricketFixture>('/admin/cricket-calendar/fixtures', body),

    updateFixture: (id: string, body: UpdateCricketFixtureBody) =>
      axiosInstance.patch<CricketFixture>(`/admin/cricket-calendar/fixtures/${id}`, body),

    deleteFixture: (id: string) => axiosInstance.delete(`/admin/cricket-calendar/fixtures/${id}`),

    listSeries: (params?: { from?: string; to?: string }) =>
      axiosInstance.get<{ items: CricketSeries[] }>('/admin/cricket-calendar/series', { params }),

    listFestivals: (params?: { from?: string; to?: string }) =>
      axiosInstance.get<{ items: CalendarFestival[] }>('/admin/cricket-calendar/festivals', {
        params,
      }),

    createFestival: (body: CalendarFestivalBody) =>
      axiosInstance.post<CalendarFestival>('/admin/cricket-calendar/festivals', body),

    updateFestival: (id: string, body: Partial<CalendarFestivalBody>) =>
      axiosInstance.patch<CalendarFestival>(`/admin/cricket-calendar/festivals/${id}`, body),

    deleteFestival: (id: string) => axiosInstance.delete(`/admin/cricket-calendar/festivals/${id}`),

    getSettings: () =>
      axiosInstance.get<CricketCalendarSettings>('/admin/cricket-calendar/settings'),

    // Saving re-applies the tracking rule to upcoming matches.
    updateSettings: (body: { trackedCountries: string[]; autoTrackLeagues: string[] }) =>
      axiosInstance.put<CricketCalendarSettings>('/admin/cricket-calendar/settings', body),

    getSyncStatus: () =>
      axiosInstance.get<CricketSyncStatus>('/admin/cricket-calendar/sync-status'),

    // 409 when the last run was less than 10 minutes ago.
    syncNow: () => axiosInstance.post<{ summary: string }>('/admin/cricket-calendar/sync'),
  },

  // Player locale (GA4 country, state, phone language)
  playerLocale: {
    coverage: () => axiosInstance.get<PlayerLocaleCoverage>('/admin/player-locale/coverage'),
  },

  // Match pushes (Live Ops)
  matchPushes: {
    queue: (params?: MatchPushQueueQuery) =>
      axiosInstance.get<{ items: MatchPushPlan[] }>('/admin/match-pushes/queue', { params }),

    previewPlan: (planId: string) =>
      axiosInstance.get<{ items: MatchPushPreviewItem[] }>(
        `/admin/match-pushes/plans/${planId}/preview`
      ),

    updatePlan: (planId: string, body: UpdateMatchPushPlanBody) =>
      axiosInstance.patch<MatchPushPlan>(`/admin/match-pushes/plans/${planId}`, body),

    sendSample: (planId: string, body: MatchPushSampleBody) =>
      axiosInstance.post<MatchPushSampleResult>(`/admin/match-pushes/plans/${planId}/sample`, body),

    planResults: (planId: string) =>
      axiosInstance.get<MatchPushPlanResults>(`/admin/match-pushes/plans/${planId}/results`),

    results: (params?: { from?: string; to?: string }) =>
      axiosInstance.get<{ items: MatchPushResultsRow[] }>('/admin/match-pushes/results', {
        params,
      }),

    languageTest: (params?: { from?: string; to?: string }) =>
      axiosInstance.get<MatchPushLanguageTest>('/admin/match-pushes/language-test', { params }),

    getSettings: () => axiosInstance.get<MatchPushSettings>('/admin/match-pushes/settings'),

    updateSettings: (body: UpdateMatchPushSettingsBody) =>
      axiosInstance.put<MatchPushSettings>('/admin/match-pushes/settings', body),

    getStatus: () => axiosInstance.get<MatchPushStatus>('/admin/match-pushes/status'),

    // Switching on needs a super admin; switching off needs liveops:create.
    setSwitch: (on: boolean) =>
      axiosInstance.put<MatchPushStatus>('/admin/match-pushes/switch', { on }),

    listTemplates: () =>
      axiosInstance.get<{ items: MatchPushTemplate[] }>('/admin/match-pushes/templates'),

    createTemplate: (body: MatchPushTemplateBody) =>
      axiosInstance.post<MatchPushTemplate>('/admin/match-pushes/templates', body),

    updateTemplate: (id: string, body: MatchPushTemplateBody) =>
      axiosInstance.patch<MatchPushTemplate>(`/admin/match-pushes/templates/${id}`, body),
  },

  // Experiments v2 (server-driven A/B tests). experiments:list/show to read,
  // experiments:create for new layers, lists and drafts, experiments:edit for
  // everything else. An action or approve on an economy test can answer 202:
  // stored for a second admin, not applied.
  experimentsV2: {
    listLayers: () => axiosInstance.get<ExpV2Layer[]>('/admin/experiments-v2/layers'),
    createLayer: (body: { key: string; name: string; description?: string }) =>
      axiosInstance.post<ExpV2Layer>('/admin/experiments-v2/layers', body),
    setHoldoutSize: (holdoutSize: number) =>
      axiosInstance.put<{ key: string; holdoutSize: number }>(
        '/admin/experiments-v2/layers/holdout/size',
        { holdoutSize }
      ),

    listLists: () => axiosInstance.get<ExpV2UserList[]>('/admin/experiments-v2/lists'),
    getList: (id: string) =>
      axiosInstance.get<ExpV2UserListDetail>(
        `/admin/experiments-v2/lists/${encodeURIComponent(id)}`
      ),
    createList: (body: { name: string; userIds: string[] }) =>
      axiosInstance.post<ExpV2UserListDetail>('/admin/experiments-v2/lists', body),
    updateList: (id: string, body: { name?: string; userIds?: string[] }) =>
      axiosInstance.put<ExpV2UserListDetail>(
        `/admin/experiments-v2/lists/${encodeURIComponent(id)}`,
        body
      ),

    playerHistory: (userId: string) =>
      axiosInstance.get<ExpV2HistoryEntry[]>(
        `/admin/experiments-v2/players/${encodeURIComponent(userId)}/history`
      ),

    list: () => axiosInstance.get<ExpV2Test[]>('/admin/experiments-v2'),
    get: (key: string) =>
      axiosInstance.get<ExpV2Detail>(`/admin/experiments-v2/${encodeURIComponent(key)}`),
    create: (body: ExpV2DraftBody) =>
      axiosInstance.post<ExpV2Detail>('/admin/experiments-v2', body),
    update: (key: string, body: ExpV2DraftUpdateBody) =>
      axiosInstance.put<ExpV2Detail>(`/admin/experiments-v2/${encodeURIComponent(key)}`, body),

    action: (key: string, action: ExpV2Action, body: ExpV2ActionBody = {}) =>
      axiosInstance.post<ExpV2ActionResult>(
        `/admin/experiments-v2/${encodeURIComponent(key)}/actions/${action}`,
        body
      ),
    // Both name the waiting request the admin saw; the server answers 409 if it was replaced since.
    approve: (key: string, requestedAt: string) =>
      axiosInstance.post<ExpV2Detail>(`/admin/experiments-v2/${encodeURIComponent(key)}/approve`, {
        requestedAt,
      }),
    reject: (key: string, requestedAt: string) =>
      axiosInstance.post<ExpV2Detail>(`/admin/experiments-v2/${encodeURIComponent(key)}/reject`, {
        requestedAt,
      }),
    setDecision: (key: string, body: ExpV2DecisionBody) =>
      axiosInstance.put<ExpV2Detail>(
        `/admin/experiments-v2/${encodeURIComponent(key)}/decision`,
        body
      ),

    listOverrides: (key: string) =>
      axiosInstance.get<ExpV2Override[]>(
        `/admin/experiments-v2/${encodeURIComponent(key)}/overrides`
      ),
    addOverrides: (key: string, body: ExpV2AddOverridesBody) =>
      axiosInstance.post<ExpV2AddOverridesResult>(
        `/admin/experiments-v2/${encodeURIComponent(key)}/overrides`,
        body
      ),
    removeOverride: (key: string, id: string) =>
      axiosInstance.delete(
        `/admin/experiments-v2/${encodeURIComponent(key)}/overrides/${encodeURIComponent(id)}`
      ),

    counts: (key: string) =>
      axiosInstance.get<ExpV2Counts>(`/admin/experiments-v2/${encodeURIComponent(key)}/counts`),
    results: (key: string, population: ExpV2Population) =>
      axiosInstance.get<ExpV2Results>(`/admin/experiments-v2/${encodeURIComponent(key)}/results`, {
        params: { population },
      }),
    timeline: (key: string) =>
      axiosInstance.get<ExpV2Timeline>(`/admin/experiments-v2/${encodeURIComponent(key)}/timeline`),
  },

  // Realtime PvP (Rivals). Settings need config:show / config:edit; the review
  // needs players:show to read and players:edit to flag or clear.
  pvp: {
    getSettings: () => axiosInstance.get<PvpConfig>('/admin/config/pvp'),

    // Partial: only the fields sent change; null clears one back to env or the
    // review default. A value out of range answers 422.
    updateSettings: (body: UpdatePvpConfigBody) =>
      axiosInstance.put<PvpConfig>('/admin/config/pvp', body),

    review: (params?: PvpReviewQuery) =>
      axiosInstance.get<PvpReviewList>('/admin/pvp/review', { params }),

    // How well the two phones of a match stay in step over a time range
    // (players:show). 400 when the range is over 31 days or from >= to.
    sync: (params?: PvpSyncQuery) => axiosInstance.get<PvpSyncDto>('/admin/pvp/sync', { params }),

    // What the PvP servers are doing now: online players, live and finished
    // matches, completion and 11 health checks (players:show). range 1h | 24h |
    // 7d (default 24h), bots include | exclude (default include); 400 otherwise.
    // The server caches each answer 10 s.
    live: (params?: PvpLiveQuery) => axiosInstance.get<PvpLiveDto>('/admin/pvp/live', { params }),

    // Match lookup and player view (players:show, read only, from the replica).
    // matches: newest first, keyset-paged by cursor; q is a match id, a player
    // id or a name prefix; the range is at most 31 days (400 otherwise).
    matches: (params?: PvpMatchesQuery) =>
      axiosInstance.get<PvpMatchesDto>('/admin/pvp/matches', { params }),

    // 404 when the match does not exist.
    match: (id: string) => axiosInstance.get<PvpMatchDetailDto>(`/admin/pvp/matches/${id}`),

    // Asks the PvP servers to end a live match (players:edit): within ~10 s, a
    // no-fault abort with both entry fees back. 404 no match, 409 not live; a
    // repeat returns the request already made.
    endMatch: (id: string, body: { note: string }) =>
      axiosInstance.post<PvpEndRequestDto>(`/admin/pvp/matches/${id}/end`, body),

    // The ball log is its own call so a slow archive never slows the match; it
    // never fails, an unreadable log answers available: false with a reason.
    balls: (id: string) => axiosInstance.get<PvpBallLogDto>(`/admin/pvp/matches/${id}/balls`),

    // 404 when the user does not exist, 400 when userId is not a UUID.
    player: (userId: string) => axiosInstance.get<PvpPlayerDto>(`/admin/pvp/players/${userId}`),

    // 404 when the player has never played PvP, 400 when userId is not a UUID.
    setReview: (userId: string, body: UpdatePvpReviewBody) =>
      axiosInstance.put<PvpReviewPlayer>(`/admin/pvp/players/${userId}/review`, body),
  },

  // Live Event Raffle
  getRafflePeriods: (eventId: string) =>
    axiosInstance.get(`/admin/live-events/${eventId}/raffle-periods`),

  selectRaffleWinner: (eventId: string, periodId: string) =>
    axiosInstance.post(`/admin/live-events/${eventId}/raffle-periods/${periodId}/select-winner`),

  // Live Event Stats
  getLiveEventStats: (eventId: string) => axiosInstance.get(`/admin/live-events/${eventId}/stats`),

  getLiveEventLeaderboard: (eventId: string, params?: Record<string, string | number | boolean>) =>
    axiosInstance.get(`/admin/live-events/${eventId}/leaderboard`, { params }),

  getLiveEventParticipants: (eventId: string, params?: Record<string, string | number | boolean>) =>
    axiosInstance.get(`/admin/live-events/${eventId}/participants`, { params }),

  // Account Transfer
  transferAccount: (playerId: string, data: TransferAccountRequest) =>
    axiosInstance.post(`/admin/players/${playerId}/transfer`, data),

  getTransferHistory: (playerId: string) =>
    axiosInstance.get(`/admin/players/${playerId}/transfer-history`),

  // Merge a mis-bound/duplicate account into this one (survivorId in path).
  mergeAccount: (survivorId: string, data: MergeAccountRequest) =>
    axiosInstance.post(`/admin/players/${survivorId}/merge`, data),

  // Player Match History
  getPlayerMatches: (playerId: string, params?: { limit?: number; offset?: number }) =>
    axiosInstance.get(`/admin/players/${playerId}/matches`, { params }),

  // Player Squads
  getPlayerSquads: (playerId: string) => axiosInstance.get(`/admin/players/${playerId}/squads`),

  // Player Cards
  getPlayerCards: (playerId: string) => axiosInstance.get(`/admin/players/${playerId}/cards`),

  // Player Devices & FCM Tokens
  getPlayerDevices: (playerId: string) => axiosInstance.get(`/admin/players/${playerId}/devices`),

  // Player Full Details (includes matches + squads)
  getPlayerFullDetails: (playerId: string) => axiosInstance.get(`/admin/players/${playerId}/full`),

  getPlayerMatchdayDirector: (playerId: string) =>
    axiosInstance.get(`/admin/players/${playerId}/matchday-director`),

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  updatePlayerMatchdayDirector: (playerId: string, data: any) =>
    axiosInstance.patch(`/admin/players/${playerId}/matchday-director`, data),

  setGASTOverride: (data: { value: number | null; reason: string }) =>
    axiosInstance.patch('/admin/players/matchday-director/gast', data),

  // Testing shortcuts
  completeStumpRewards: (playerId: string) =>
    axiosInstance.post(`/admin/players/${playerId}/complete-stump`),

  resetStumpRewards: (playerId: string) =>
    axiosInstance.post(`/admin/players/${playerId}/reset-stump`),

  setPlayerDivision: (playerId: string, fanRating: number) =>
    axiosInstance.post(`/admin/players/${playerId}/set-division`, { fanRating }),

  // Enhanced Purchase Search
  searchPurchases: (params: PurchaseSearchFilters) =>
    axiosInstance.get('/admin/purchases', { params }),

  getPurchaseDetail: (id: string) => axiosInstance.get(`/admin/purchases/${id}`),

  getUserPurchases: (userId: string, params?: Record<string, string | number>) =>
    axiosInstance.get(`/admin/purchases/user/${userId}`, { params }),

  getUserPurchaseStats: (userId: string) =>
    axiosInstance.get(`/admin/purchases/user/${userId}/stats`),

  grantPurchase: (data: { userId: string; productId: string; orderId?: string; reason: string }) =>
    axiosInstance.post('/admin/purchases/grant', data),

  // Admin Auth
  changePassword: (data: { currentPassword: string; newPassword: string }) =>
    axiosInstance.put('/admin/auth/password', data),

  getMyPermissions: () =>
    axiosInstance.get<{
      role: { id: string | null; type: string | null; name: string | null };
      structured: Record<string, string[]>;
      flat: string[];
      isSuperAdmin: boolean;
    }>('/admin/auth/me/permissions'),

  /**
   * Fill every empty slot in the user's home squad (XI + subs + extras)
   * using their existing unused cards only — never generates new ones.
   * XI is strict role-matched; subs take any non-WK card; extras take any
   * leftover card (including spare WK). Idempotent.
   */
  fillHomeSquadFromCollection: (userId: string) =>
    axiosInstance.post<{
      filled: number;
      cardsAssigned: Array<{ position: string; playerCardId: string }>;
      byCategory: { xi: number; subs: number; extras: number };
      finalPlayerCount: number;
      finalTotalRating: number;
    }>(`/admin/players/${userId}/squad/fill`),

  /**
   * Assign a player card to a specific squad slot, or clear the slot
   * (playerCardId=null). Backend is transactional and handles the
   * (squadId, playerCardId) unique index by vacating any other slot in
   * the same squad already holding that card.
   */
  assignSquadSlot: (
    userId: string,
    squadType: 'home' | 'away',
    position: string,
    playerCardId: string | null
  ) =>
    axiosInstance.patch<{
      squadId: string;
      position: string;
      playerCardId: string | null;
    }>(`/admin/players/${userId}/squad/${squadType}/slot/${position}`, {
      playerCardId,
    }),

  /**
   * Skip the user's FTUE — admin-side flow that **preserves existing cards**.
   * Phase 1 fills empty XI + sub slots with the user's unused cards
   * (role-aware); Phase 2 generates one role-matched card per still-empty
   * slot so the user ends up with FTUE completed AND a full 11+4 squad,
   * without wiping anything they already owned.
   */
  skipPlayerFtue: (userId: string) =>
    axiosInstance.post<{
      previousStep: string;
      currentStep: string;
      isCompleted: boolean;
      assignedExisting: number;
      generated: number;
    }>(`/admin/players/${userId}/ftue/skip`),

  /**
   * Delete a player card from the user's collection. Backend refuses with
   * 400 if the card is in any squad slot — clear the slot first.
   */
  deletePlayerCard: (userId: string, cardId: string) =>
    axiosInstance.delete<{ deleted: boolean; cardId: string }>(
      `/admin/players/${userId}/cards/${cardId}`
    ),

  updatePurchaseStatus: (id: string, data: { status: string; reason?: string }) =>
    axiosInstance.put(`/admin/purchases/${id}/status`, data),

  refundPurchase: (id: string, data: { reason: string; revokeItems?: boolean }) =>
    axiosInstance.post(`/admin/purchases/${id}/refund`, data),

  /** Asks Google about a FAILED Google Play purchase again; grants it if Google now says purchased. */
  recheckGooglePurchase: (id: string) =>
    axiosInstance.post<GoogleRecheckResult>(`/admin/purchases/${id}/recheck-google`),

  /** Runs Google's voided-purchase sweep (last 30 days) now. Can take tens of seconds. */
  syncGoogleVoidedPurchases: () =>
    axiosInstance.post<VoidedSyncCounts>('/admin/purchases/refunds/voided-sync'),

  getRevenueAnalytics: (params?: Record<string, string>) =>
    axiosInstance.get('/admin/purchases/analytics/revenue', { params }),

  getActiveSubscriptions: (params?: Record<string, string | number>) =>
    axiosInstance.get('/admin/purchases/subscriptions/active', { params }),

  getSubscriptionSummary: () => axiosInstance.get('/admin/purchases/subscriptions/summary'),

  // Tester Registry
  listTesters: () => axiosInstance.get('/admin/purchases/testers'),
  addTester: (data: { platform: string; identifier: string; label: string; notes?: string }) =>
    axiosInstance.post('/admin/purchases/testers', data),
  removeTester: (id: string) => axiosInstance.delete(`/admin/purchases/testers/${id}`),
  toggleTester: (id: string) => axiosInstance.patch(`/admin/purchases/testers/${id}/toggle`),

  // Diagnostics
  getDiagnostics: () => axiosInstance.get('/admin/diagnostics'),

  // Cron Jobs
  getCronJobs: () => axiosInstance.get('/admin/cron-jobs'),

  getCronJobStats: () => axiosInstance.get('/admin/cron-jobs/stats'),

  getCronJob: (jobName: string) => axiosInstance.get(`/admin/cron-jobs/${jobName}`),

  enableCronJob: (jobName: string) => axiosInstance.post(`/admin/cron-jobs/${jobName}/enable`),

  disableCronJob: (jobName: string) => axiosInstance.post(`/admin/cron-jobs/${jobName}/disable`),

  toggleCronJob: (jobName: string) => axiosInstance.post(`/admin/cron-jobs/${jobName}/toggle`),

  enableAllCronJobs: () => axiosInstance.post('/admin/cron-jobs/enable-all'),

  disableAllCronJobs: () => axiosInstance.post('/admin/cron-jobs/disable-all'),

  // Store Sync
  getStoreSyncStatus: () => axiosInstance.get('/admin/store-sync/status'),

  getStoreSyncProducts: (params?: Record<string, string | number | boolean>) =>
    axiosInstance.get('/admin/store-sync/products', { params }),

  triggerStoreSync: (store?: 'google_play' | 'app_store') =>
    axiosInstance.post('/admin/store-sync/trigger', { store }),

  getStoreSyncHistory: (limit?: number) =>
    axiosInstance.get('/admin/store-sync/history', { params: { limit } }),

  getStoreSyncMismatches: () => axiosInstance.get('/admin/store-sync/mismatches'),

  getStoreOnlyProducts: () => axiosInstance.get('/admin/store-sync/store-only'),

  getRegionalPricing: (sku: string, store?: 'google_play' | 'app_store') =>
    axiosInstance.get(`/admin/store-sync/pricing/${sku}`, { params: { store } }),

  linkStoreProduct: (syncId: string, productId: string) =>
    axiosInstance.post(`/admin/store-sync/link/${syncId}`, { productId }),

  validateStoreCatalogs: () => axiosInstance.get('/admin/store-sync/validate'),

  // Deeplinks
  getDeeplinks: (params?: Record<string, string | number | boolean>) =>
    axiosInstance.get('/admin/deeplinks', { params }),

  createDeeplink: (data: Record<string, unknown>) => axiosInstance.post('/admin/deeplinks', data),

  getDeeplink: (id: string) => axiosInstance.get(`/admin/deeplinks/${id}`),

  updateDeeplink: (id: string, data: Record<string, unknown>) =>
    axiosInstance.put(`/admin/deeplinks/${id}`, data),

  activateDeeplink: (id: string) => axiosInstance.post(`/admin/deeplinks/${id}/activate`),

  deactivateDeeplink: (id: string) => axiosInstance.post(`/admin/deeplinks/${id}/deactivate`),

  deleteDeeplink: (id: string) => axiosInstance.delete(`/admin/deeplinks/${id}`),

  // Hot Events
  getHotEvents: (params?: Record<string, string | number | boolean>) =>
    axiosInstance.get('/admin/hot-events', { params }),

  createHotEvent: (data: Record<string, unknown>) => axiosInstance.post('/admin/hot-events', data),

  getHotEvent: (id: string) => axiosInstance.get(`/admin/hot-events/${id}`),

  updateHotEvent: (id: string, data: Record<string, unknown>) =>
    axiosInstance.put(`/admin/hot-events/${id}`, data),

  deleteHotEvent: (id: string) => axiosInstance.delete(`/admin/hot-events/${id}`),

  // Branding (brand-collab campaigns)
  getBrandCampaigns: (params?: Record<string, string | number | boolean>) =>
    axiosInstance.get('/admin/branding', { params }),

  getBrandCampaign: (id: string) => axiosInstance.get(`/admin/branding/${id}`),

  createBrandCampaign: (data: Record<string, unknown>) =>
    axiosInstance.post('/admin/branding', data),

  updateBrandCampaign: (id: string, data: Record<string, unknown>) =>
    axiosInstance.put(`/admin/branding/${id}`, data),

  deleteBrandCampaign: (id: string) => axiosInstance.delete(`/admin/branding/${id}`),

  getBrandingVersion: () => axiosInstance.get('/admin/branding/version'),

  bumpBrandingVersion: (body?: {
    segment?: 'major' | 'minor' | 'patch';
    direction?: 'up' | 'down';
  }) => axiosInstance.post('/admin/branding/version/bump', body ?? {}),

  setBrandingVersion: (version: string) =>
    axiosInstance.put('/admin/branding/version', { version }),

  // Inbox
  getInboxMessages: (params?: Record<string, string | number | boolean>) =>
    axiosInstance.get('/admin/inbox', { params }),

  createInboxMessage: (data: Record<string, unknown>) => axiosInstance.post('/admin/inbox', data),

  getInboxMessage: (id: string) => axiosInstance.get(`/admin/inbox/${id}`),

  updateInboxMessage: (id: string, data: Record<string, unknown>) =>
    axiosInstance.put(`/admin/inbox/${id}`, data),

  deleteInboxMessage: (id: string) => axiosInstance.delete(`/admin/inbox/${id}`),

  // Missions
  getMissions: (params?: Record<string, string | number | boolean>) =>
    axiosInstance.get('/admin/missions', { params }),

  createMission: (data: Record<string, unknown>) => axiosInstance.post('/admin/missions', data),

  getMission: (id: string) => axiosInstance.get(`/admin/missions/${id}`),

  updateMission: (id: string, data: Record<string, unknown>) =>
    axiosInstance.put(`/admin/missions/${id}`, data),

  deleteMission: (id: string) => axiosInstance.delete(`/admin/missions/${id}`),

  // Cohorts
  getCohorts: (params?: Record<string, string | number | boolean>) =>
    axiosInstance.get('/admin/cohorts', { params }),

  createCohort: (data: Record<string, unknown>) => axiosInstance.post('/admin/cohorts', data),

  getCohort: (id: string) => axiosInstance.get(`/admin/cohorts/${id}`),

  updateCohort: (id: string, data: Record<string, unknown>) =>
    axiosInstance.put(`/admin/cohorts/${id}`, data),

  deleteCohort: (id: string) => axiosInstance.delete(`/admin/cohorts/${id}`),

  /** Starts a background recalculation of every dynamic cohort, or of one. 409 if one is running. */
  startCohortRecalculation: (cohortId?: string) =>
    axiosInstance.post<CohortRecalculationStatus>(
      '/admin/cohorts/recalculate',
      cohortId ? { cohortId } : {}
    ),

  /** What a cohort's members have spent: summary, bands, products, one page of members by spend. */
  getCohortSpend: (id: string, params?: { limit?: number; offset?: number }) =>
    axiosInstance.get<CohortSpendReport>(`/admin/cohorts/${id}/spend`, { params }),

  /** The running recalculation, else the last finished one. */
  getCohortRecalculationStatus: () =>
    axiosInstance.get<CohortRecalculationStatus>('/admin/cohorts/recalculate/status'),

  // Custom Ads
  getCustomAds: () => axiosInstance.get('/admin/custom-ads'),

  getCustomAd: (id: string) => axiosInstance.get(`/admin/custom-ads/${id}`),

  createCustomAd: (data: Record<string, unknown>) => axiosInstance.post('/admin/custom-ads', data),

  updateCustomAd: (id: string, data: Record<string, unknown>) =>
    axiosInstance.put(`/admin/custom-ads/${id}`, data),

  deleteCustomAd: (id: string) => axiosInstance.delete(`/admin/custom-ads/${id}`),

  toggleCustomAdActive: (id: string) =>
    axiosInstance.patch(`/admin/custom-ads/${id}/toggle-active`),

  invalidateCustomAdsCache: () => axiosInstance.post('/admin/custom-ads/invalidate-cache'),

  invalidateCustomAdsCacheByLocation: (location: string) =>
    axiosInstance.post(`/admin/custom-ads/invalidate-cache/${location}`),

  // Experiments (A/B tests)
  getExperiments: (params?: Record<string, unknown>) =>
    axiosInstance.get('/admin/experiments', { params }),

  getExperiment: (key: string) => axiosInstance.get(`/admin/experiments/${key}`),

  createExperiment: (data: Record<string, unknown>) =>
    axiosInstance.post('/admin/experiments', data),

  updateExperiment: (key: string, data: Record<string, unknown>) =>
    axiosInstance.put(`/admin/experiments/${key}`, data),

  startExperiment: (key: string) => axiosInstance.post(`/admin/experiments/${key}/start`),

  stopExperiment: (key: string) => axiosInstance.post(`/admin/experiments/${key}/stop`),

  deleteExperiment: (key: string) => axiosInstance.delete(`/admin/experiments/${key}`),

  // Export functions
  exportPurchases: (fromDate?: string, toDate?: string) =>
    axiosInstance.get('/admin/export/purchases', {
      params: { fromDate, toDate },
      responseType: 'blob',
    }),

  exportRevenue: (fromDate?: string, toDate?: string) =>
    axiosInstance.get('/admin/export/revenue', {
      params: { fromDate, toDate },
      responseType: 'blob',
    }),

  exportInGameCurrencyBurn: (fromDate?: string, toDate?: string) =>
    axiosInstance.get('/admin/export/in-game-currency-burn', {
      params: { fromDate, toDate },
      responseType: 'blob',
    }),

  exportRiskAnalytics: (fromDate?: string, toDate?: string) =>
    axiosInstance.get('/admin/export/risk-analytics', {
      params: { fromDate, toDate },
      responseType: 'blob',
    }),

  exportFtueBugCohort: () =>
    axiosInstance.get('/admin/export/cohort/ftue-bug', {
      responseType: 'blob',
    }),

  exportPartialSquadCohort: () =>
    axiosInstance.get('/admin/export/cohort/partial-squad', {
      responseType: 'blob',
    }),

  exportPlayers: (fromDate?: string, toDate?: string) =>
    axiosInstance.get('/admin/export/players', {
      params: { fromDate, toDate },
      responseType: 'blob',
    }),

  exportPlayerData: (includeBaseStats?: boolean) =>
    axiosInstance.get('/admin/player-data/export', {
      params: { includeBaseStats },
      responseType: 'blob',
    }),

  getPlayerOwnershipStatus: () =>
    axiosInstance.get<PlayerOwnershipStatus>('/admin/player-data/ownership'),

  /** Starts a background recount and returns the status at once (GET: the read-only switch blocks POSTs here). */
  refreshPlayerOwnership: () =>
    axiosInstance.get<PlayerOwnershipStatus>('/admin/player-data/ownership', {
      params: { refresh: true },
    }),

  exportDAU: (fromDate?: string, toDate?: string) =>
    axiosInstance.get('/admin/export/dau', {
      params: { fromDate, toDate },
      responseType: 'blob',
    }),

  exportAuditLogs: (fromDate?: string, toDate?: string) =>
    axiosInstance.get('/admin/export/audit-logs', {
      params: { fromDate, toDate },
      responseType: 'blob',
    }),

  exportEconomyPerDivision: () =>
    axiosInstance.get('/admin/export/economy-per-division', {
      responseType: 'blob',
    }),

  exportGlobalAiAnalytics: (fromDate?: string, toDate?: string) =>
    axiosInstance.get('/admin/export/ai-analytics', {
      params: { fromDate, toDate },
      responseType: 'blob',
    }),

  exportUserAiAnalytics: (userId: string, fromDate?: string, toDate?: string) =>
    axiosInstance.get(`/admin/export/ai-analytics/user/${userId}`, {
      params: { fromDate, toDate },
      responseType: 'blob',
    }),

  // §3.1 — Match Analytics (by-type + custom overs + per-day)
  exportMatchBreakdown: (fromDate?: string, toDate?: string) =>
    axiosInstance.get('/admin/export/match-breakdown', {
      params: { fromDate, toDate },
      responseType: 'blob',
    }),

  // §3.2 — Retention Funnel snapshot (DAU/WAU/MAU/at-risk/churned)
  exportRetentionFunnel: () =>
    axiosInstance.get('/admin/export/retention-funnel', {
      responseType: 'blob',
    }),

  // §3.3 — Funnel Drop-off (users with 0 matches, bucketed)
  exportFunnelDropOff: () =>
    axiosInstance.get('/admin/export/funnel-drop-off', {
      responseType: 'blob',
    }),

  // §6.5 — Suspect Matches (cheat-detection flagged)
  exportSuspectMatches: (fromDate?: string, toDate?: string) =>
    axiosInstance.get('/admin/export/suspect-matches', {
      params: { fromDate, toDate },
      responseType: 'blob',
    }),

  // §3.4 — Players with optional cohort + divisionTier filters
  exportPlayersFiltered: (params: {
    fromDate?: string;
    toDate?: string;
    cohort?: string;
    divisionTier?: number;
  }) =>
    axiosInstance.get('/admin/export/players', {
      params,
      responseType: 'blob',
    }),

  // Daily Deals
  /**
   * Every deal config row — shared and per-config-version variants alike.
   * Omitting the filter is deliberate: the page resolves "what this build sees"
   * itself so it can show a variant and the shared row it overrides together.
   */
  getDailyDealsConfigs: (configVersionId?: string | null) =>
    axiosInstance.get<DailyDealConfig[]>('/admin/daily-deals/configs', {
      params: configVersionId ? { configVersionId } : undefined,
    }),

  getDailyDealConfig: (dealType: string, configVersionId?: string | null) =>
    axiosInstance.get<DailyDealConfig>(`/admin/daily-deals/configs/${dealType}`, {
      params: configVersionId ? { configVersionId } : undefined,
    }),

  /** Omit `configVersionId` to edit the shared row every build inherits. */
  updateDailyDealConfig: (
    dealType: string,
    data: UpdateDailyDealConfigRequest,
    configVersionId?: string | null
  ) =>
    axiosInstance.put<DailyDealConfig>(`/admin/daily-deals/configs/${dealType}`, data, {
      params: configVersionId ? { configVersionId } : undefined,
    }),

  toggleDailyDeal: (dealType: string, configVersionId?: string | null) =>
    axiosInstance.post<{ isActive: boolean }>(
      `/admin/daily-deals/configs/${dealType}/toggle`,
      undefined,
      { params: configVersionId ? { configVersionId } : undefined }
    ),

  /**
   * Fork the shared row into an override for one config version. The new row
   * starts as a copy, so builds on that version keep seeing what they saw until
   * it is edited.
   */
  createDailyDealVariant: (dealType: string, configVersionId: string) =>
    axiosInstance.post<DailyDealConfig>(`/admin/daily-deals/configs/${dealType}/variants`, {
      configVersionId,
    }),

  /** Drop an override; builds on that version fall back to the shared row. */
  deleteDailyDealVariant: (dealType: string, configVersionId: string) =>
    axiosInstance.delete(`/admin/daily-deals/configs/${dealType}/variants/${configVersionId}`),

  resetUserCooldown: (data: ResetCooldownRequest) =>
    axiosInstance.delete<{ success: boolean }>('/admin/daily-deals/user-cooldown', { data }),

  regenerateDailySeed: (data: RegenerateSeedRequest) =>
    axiosInstance.post('/admin/daily-deals/regenerate-seed', data),

  getDailyDealsAnalytics: (params?: { startDate?: string; endDate?: string; dealType?: string }) =>
    axiosInstance.get<DailyDealAnalytics[]>('/admin/daily-deals/analytics', { params }),

  getDailyDealPurchases: (params: PurchaseHistoryFilters) =>
    axiosInstance.get<DailyDealPurchaseHistoryResponse>('/admin/daily-deals/purchases', { params }),

  getUserDailyDealsStatus: (userId: string) =>
    axiosInstance.get<UserDailyDealsStatus>(`/admin/daily-deals/user/${userId}`),

  exportDailyDealPurchases: (params?: ExportFilters) =>
    axiosInstance.get('/admin/daily-deals/export/purchases', {
      params,
      responseType: 'blob',
    }),

  exportDailyDealAnalytics: (params?: ExportFilters) =>
    axiosInstance.get('/admin/daily-deals/export/analytics', {
      params,
      responseType: 'blob',
    }),

  // ============== Match / Retention / Funnel Analytics (post-launch v0.1) ==============

  /** §3.1 — matches grouped by matchType + custom-match overs + per-day series */
  getMatchBreakdown: (params?: AnalyticsDateFilters) =>
    axiosInstance.get('/admin/analytics/match-breakdown', { params }),

  /** §3.2 — DAU/WAU/MAU + at-risk + likely-churned snapshot */
  getRetentionFunnel: () => axiosInstance.get('/admin/analytics/retention-funnel'),

  /** §3.3 — users with zero matches, segmented by registration / login / FTUE state */
  getFunnelDropOff: () => axiosInstance.get('/admin/analytics/funnel-drop-off'),

  /** §3.6 — real-money revenue, split by Google Pay vs App Store, with refunds + classification filters */
  getRevenueBreakdown: (params?: AnalyticsDateFilters) =>
    axiosInstance.get('/admin/analytics/revenue-breakdown', { params }),

  // ============== Matchday-Director Config (§3.7) ==============

  /** Omit `configVersionId` for the shared config every build inherits. */
  getMatchdayDirectorConfig: (configVersionId?: string | null) =>
    axiosInstance.get<MatchdayDirectorConfigResponse>('/admin/matchday-director/config', {
      params: configVersionId ? { configVersionId } : undefined,
    }),

  /**
   * `comment` (3-500 chars, required) is recorded in the change history only —
   * it is never stored on the config. 400 "No changes to save" when nothing differs.
   */
  updateMatchdayDirectorConfig: (
    /** Any subset of the sections: the backend keeps every section left out. */
    body: Partial<MatchdayDirectorConfigPayload>,
    comment: string,
    configVersionId?: string | null
  ) =>
    axiosInstance.put<MatchdayDirectorConfigResponse>(
      '/admin/matchday-director/config',
      { ...body, comment },
      { params: configVersionId ? { configVersionId } : undefined }
    ),

  resetMatchdayDirectorConfig: (comment: string, configVersionId?: string | null) =>
    axiosInstance.post<MatchdayDirectorConfigResponse>(
      '/admin/matchday-director/config/reset',
      { comment },
      { params: configVersionId ? { configVersionId } : undefined }
    ),

  /**
   * Remove one config version's override so its builds inherit the shared
   * config again. Requires a config version — the shared row cannot be deleted.
   */
  deleteMatchdayDirectorOverride: (configVersionId: string, comment: string) =>
    axiosInstance.delete('/admin/matchday-director/config', {
      params: { configVersionId },
      data: { comment },
    }),

  /**
   * Who changed what and why, newest first. No `configVersionId` is the shared
   * config's history; `all` returns every scope's.
   */
  getMatchdayDirectorConfigHistory: ({
    configVersionId,
    all,
    limit,
    offset,
  }: MatchdayDirectorConfigHistoryParams = {}) =>
    axiosInstance.get<MatchdayDirectorConfigHistoryResponse>(
      '/admin/matchday-director/config/history',
      {
        params: {
          ...(all ? { all: 'true' } : configVersionId ? { configVersionId } : {}),
          ...(limit !== undefined ? { limit } : {}),
          ...(offset !== undefined ? { offset } : {}),
        },
      }
    ),

  /** §6.5 — list matches flagged by server-side cheat detection. */
  /** One player risk list, paged: total counts every flagged player in the window. */
  // Daily Challenge (Challenger Mode): wcc4-backend /admin/challenger.
  challengerList: (params: { status?: string; from?: string; to?: string } = {}) =>
    axiosInstance.get('/admin/challenger/challenges', { params }),
  challengerGet: (id: string) => axiosInstance.get(`/admin/challenger/challenges/${id}`),
  challengerCreate: (body: unknown) => axiosInstance.post('/admin/challenger/challenges', body),
  challengerUpdate: (id: string, body: unknown) =>
    axiosInstance.patch(`/admin/challenger/challenges/${id}`, body),
  challengerPublish: (id: string) =>
    axiosInstance.post(`/admin/challenger/challenges/${id}/publish`),
  challengerArchive: (id: string) =>
    axiosInstance.post(`/admin/challenger/challenges/${id}/archive`),
  challengerDuplicate: (id: string) =>
    axiosInstance.post(`/admin/challenger/challenges/${id}/duplicate`),
  challengerValidate: (body: unknown) =>
    axiosInstance.post('/admin/challenger/challenges/validate', body),
  challengerStats: (id: string) => axiosInstance.get(`/admin/challenger/challenges/${id}/stats`),
  challengerSettings: () => axiosInstance.get('/admin/challenger/settings'),
  challengerSaveSettings: (body: unknown) => axiosInstance.put('/admin/challenger/settings', body),

  getRiskList: (list: RiskListName, filters: AnalyticsDateFilters & PageFilters = {}) =>
    axiosInstance.get(`/admin/analytics/risk/lists/${list}`, { params: filters }),

  /** Leaderboard page opened from the game's ad, and returns to the app (analytics:list). */
  getLeaderboardPageOpens: (days: number) =>
    axiosInstance.get('/admin/leaderboards/page-opens', { params: { days } }),

  getSuspectMatches: (filters: AnalyticsDateFilters & PageFilters = {}) =>
    axiosInstance.get('/admin/analytics/risk/suspect-matches', {
      params: filters,
    }),

  /** Players whose client earns went past the reward config's IST-day limit. */
  getEarnLimitFlags: (filters: AnalyticsDateFilters & PageFilters = {}) =>
    axiosInstance.get('/admin/analytics/risk/earn-flags', {
      params: filters,
    }),

  /** Rare-or-better card generate calls (audit only), per player, IST day and rarity. */
  getCardGenerateAudit: (filters: AnalyticsDateFilters & PageFilters = {}) =>
    axiosInstance.get('/admin/analytics/risk/card-generates', {
      params: filters,
    }),
};

/** A page of a list: `limit` rows from `offset`. */
export interface PageFilters {
  offset?: number;
  limit?: number;
}

/** Date-range filters shared by the post-launch v0.1 analytics endpoints. */
export interface AnalyticsDateFilters {
  period?:
    | 'today'
    | 'yesterday'
    | 'last_7_days'
    | 'last_30_days'
    | 'last_90_days'
    | 'all'
    | 'custom';
  startDate?: string; // YYYY-MM-DD when period='custom'
  endDate?: string;
}
